<div class="heading skin-detail-heading" (resize)="handleResize($event)">
  @if (canGoBack()) {
    <app-button2 appearance="secondary-ghost" size="s" aria-label="Go back" (click)="handleGoBack()">
      <app-icon name="chevron-left" />
    </app-button2>
  }

  <app-input
    class="input"
    appearance="sapphire-inline"
    [inputFormControl]="form.controls.title"
    placeholder="New Folder"
    [errorPlaceholder]="'Add title'"
    [withErrors]="submitted"
    size="s"
  />

  <div class="icons">
    <div [appPreventDirtyClick]="dirtyFormRef" class="contents">
      <!-- [MUST-002] 'Bug' debug quick-action hidden per task; uncomment to re-enable
      <stch-quick-action
        *ngIf="!contracted"
        appearance="sapphire-toggle"
        size="m"
        icon="bug"
        [active]="selectedTab === 'debug'"
        matTooltip="Debug"
        (onClick)="selectedTab = 'debug'"
      />
      -->

      <stch-quick-action
        *ngIf="!contracted"
        appearance="sapphire-toggle"
        size="m"
        icon="sapphire-archive2"
        [active]="form.controls.archived.value"
        [matTooltip]="form.controls.archived.value ? Labels.RestoreFromArchive : Labels.Archive"
        [disabled]="!folder?.id"
        (onClick)="archive()"
      />

      <stch-trash-action
        *ngIf="!contracted"
        [disabled]="!folder?.id"
        [deleted]="form.controls.deleted.value"
        (onClick)="delete()"
      />

      <!-- SBUX-785 v1: containers are not shareable (only leaf entities). Re-enable later.
      <stch-quick-action
        *ngIf="!contracted"
        appearance="sapphire-toggle"
        size="m"
        icon="sapphire-share"
        matTooltip="Share"
        [disabled]="!folder?.id"
        [stchShareContextMenu]="folder"
        [stchShareContextMenuDisabled]="!folder?.id"
      />
      -->

      <app-button
        appearance="sapphire-secondary-ghost"
        size="xs"
        icon="sapphire-ellipsis"
        matTooltip="More options"
        ariaLabel="More options"
        [disabled]="!folder?.id"
        (appFolderContextMenuDetails)="selectedTab = 'details'"
        [appFolderContextMenu]="folder"
        [appFolderContextMenuDisabled]="!folder?.id"
        appFolderContextMenuMode="popover"
        [printEnabled]="true"
      />
    </div>

    @if (showCloseButton) {
      <app-button appearance="sapphire-secondary-ghost" size="xs" icon="sapphire-close" (click)="handleClose()" />
    }
  </div>
</div>

<!--
  #1016 r2 rows 2a-2c, adopted for folders (#1054 r2). Renders only inside an accepted team and only for
  a NEW folder: `workspaceIdentity()` is undefined in a personal workspace and for a pending invite, and
  a personal workspace has no second destination to choose between, so there is no control rather than a
  disabled one.

  Placed BELOW `.heading`, with the back arrow left inside it — which is how all four hosts that adopted
  this control before files solved the same collision (`note-form.component.html:87`,
  `notebook-form:97`, `task-form:84`, `project-form:119`). `dp-form-back-arrow.spec.ts` (SBUX-23) needs
  the arrow to precede the form's first content element; mounting the selector first pushed the arrow
  down, and round 2 answered that by hoisting the arrow out of `.heading` instead — which satisfied the
  source-order guard but dropped the arrow out of every container, losing `.heading`'s padding,
  `align-items: center` and 40 px height and pushing the form down whenever `canGoBack()` is true
  (#1134 review F4). The arrow belongs in the heading; the selector belongs after it.
-->
@if (!folder?.id && workspaceIdentity(); as identity) {
  <app-tenancy-selector
    [spaceTitle]="identity.spaceTitle"
    [spaceTenantId]="identity.spaceTenantId"
    [memberTenantId]="identity.memberTenantId"
    itemNoun="folder"
    [locked]="!!folder?.accountId || form.controls.uploads.value.length > 0"
    [lockedReason]="folder?.accountId ? 'This draft already belongs to a workspace.' : null"
    [(tenantId)]="sharingTenantId"
  />
}

<app-stitch-timestamps class="block" [createdAt]="folder?.createdAt" [updatedAt]="folder?.updatedAt" />

<div class="tabs skin-detail-tabs">
  <app-tabs
    appearance="sapphire"
    [values]="tabsWithCounts()"
    [(value)]="selectedTab"
    size="m"
  />

  <div class="filler"></div>

  <!-- [MUST-001] 'Assign' button hidden per task; uncomment to re-enable

  <app-button appearance="sapphire-secondary" size="xs" label="Assign" />

  -->
  <stch-knowledge-button [active]="displayKnowledge" (click)="showKnowledge()" />
</div>

<div
  *ngIf="form"
  class="form"
  appDroppable
  appDroppableHoverClass="drag-over"
  [appDroppablePredicate]="dndPredicate"
  (appDroppableDrop)="dndDrop($event)"
  [appDroppableZIndex]="0"
>
  <div class="drop-area-border"></div>

  @if (selectedTab === 'stitch' && hasKnowledgeHubAccess()) {
    <app-entity-hub-search [contactId]="folder?.id" />
  }

  <div class="form-heading">
    <div class="form-heading-nav">
      <div class="form-heading-nav-separator"></div>

    </div>

    <div *ngIf="displayKnowledge" class="form-heading-nav-underline"></div>

    <!-- One Knowledge box, rows in the skin's order: Knots · Tags · Connections. -->
    <div class="form-knowledge" [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())">
      <app-knots
        class="form-knots"
        [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
        [stitchItems]="folder | toArray"
        [control]="form.controls['knots']"
        [stateKey]="StateKey.knotsList"
        [withControls]="selectedTab !== 'stitch'"
        [isHubMode]="selectedTab === 'stitch' && hasKnowledgeHubAccess()"
        [hubEntityUid]="folder?.id"
      />

      <app-tags
        class="form-tags"
        [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
        [stitchItems]="[folder]"
        [control]="form.controls['tags']"
        [stateKey]="StateKey.tagsList"
        [withControls]="selectedTab !== 'stitch'"
        [isHubMode]="selectedTab === 'stitch' && hasKnowledgeHubAccess()"
        [hubEntityUid]="folder?.id"
      />

      <app-connections
        class="form-knots"
        [class.hidden]="!displayKnowledge || (selectedTab === 'stitch' && !hasKnowledgeHubAccess())"
        [stitchItems]="[folder]"
        [control]="form.controls['connections']"
        [stateKey]="StateKey.connectionsList"
        [withControls]="selectedTab !== 'stitch'"
        [isHubMode]="selectedTab === 'stitch' && hasKnowledgeHubAccess()"
        [hubEntityUid]="folder?.id"
      />
    </div>
  </div>

  @if (selectedTab === 'folder' && folder?.id && !folder.archived && !folder.deleted) {
    <app-quick-form
      class="block mx-4 mb-2 shrink-0"
      trigger="button"
      placeholder="Add New File"
      [collapseKey]="folder.id"
    >
      <app-file-quick-form [folder]="folder" [tenantItems]="[folder]" />
    </app-quick-form>
  }

  <!-- stitch-app-shell ContainerItems: the list's sort & filter on a bar of its own right above the list, on
       the far right over a 1px line, so it reads as the list's (Johanna, 2026-10-21; it sat in the tab row). -->
  <div class="container-list-bar" *ngIf="selectedTab === 'folder'">
    <app-context-menu-state-indicator svgIcon="filters" appFilesListContextMenu />
  </div>
  <app-scroll
    *ngIf="(selectedTab !== 'stitch' || (permissionsLoaded() && !hasKnowledgeHubAccess()))"
    [ngSwitch]="selectedTab"
    class="form-scroll"
    (top)="displayScrollShadow = !$event"
  >
    <div *ngIf="displayScrollShadow" class="form-scroll-shadow"></div>

    <ng-template #unsavedFolderFilesTpl>
      <app-no-content-stub imageSrc="assets/placeholders/placeholder-files.svg">
        <div class="flex max-w-full flex-col items-center gap-2">
          <p class="text-base font-medium text-gray-900 text-center">No files</p>
          <p class="text-sm text-gray-600 text-center">Save your folder to start adding files</p>
        </div>
      </app-no-content-stub>
    </ng-template>

    <app-files-list
      *ngSwitchCase="'folder'"
      class="h-full"
      [filters]="filesFilters"
      [tenantItems]="[folder]"
      [notFoundTemplate]="folder?.id ? undefined : unsavedFolderFilesTpl"
      (openFile)="handleClickItem($event)"
      (clickFile)="handleClickItem($event)"
      (doubleClickFile)="handleDblClickItem($event)"
    />

    <div *ngSwitchCase="'details'" class="form-kit" [class.contracted]="contracted">
      <section class="form-kit-section">
        @if (false) {
          <!-- HIDDEN FOR VERSION 1-->
          <div class="form-kit-field">
            <span class="form-kit-label">Parent Folder</span>
            <app-autocomplete-input
              appearance="sapphire-outline"
              placeholder="Parent Folder"
              [suggestions]="parentSuggestions"
              [inputFormControl]="form.controls.parentId"
              size="s"
              [withErrors]="submitted"
              [validationMessages]="{
                circular: 'Circular dependency',
                deep: 'Too deep',
              }"
              [withClear]="true"
            />
          </div>
        }
      </section>

      <section class="form-kit-section">
        <stch-item-description-field
          [control]="form.controls.description"
          [item]="folder"
          [tagsControl]="form.controls['tags']"
          [knotsControl]="form.controls['knots']"
        />
      </section>
    </div>

    <app-attachments-list
      class="form-attachments"
      *ngSwitchCase="'attachments'"
      [stitchItem]="stitchItem"
      [tenantItems]="tenantCarrier()"
      [inputControl]="form.controls.uploads"
      [contextMenuStateVariant]="attachmentsMenuStateVariant"
      (listStateChange)="attachmentsListState = $event"
      (onClickItem)="handleClickItem($event)"
      (onDblClickItem)="handleDblClickItem($event)"
      (uploading)="onUploadingChange($event)"
    />

    <app-context-menu-state-indicator
      *ngSwitchCase="'stitch'"
      class="block ml-auto w-fit mt-5 mr-4"
      svgIcon="filters"
      appStitchListContextMenu
      (appStitchListContextMenuChange)="stitchListState = $event"
    />

    <app-linked-info-list
      *ngSwitchCase="'stitch'"
      class="form-linked-info"
      [stitch]="folder"
      [state]="stitchListState"
      [placeholderSubtitle]="folder?.id ? undefined : 'Save to start Stitching items'"
      (clickStitchItem)="handleClickItem($event)"
      (dblClickStitchItem)="handleDblClickItem($event)"
    />

    <stch-comments *ngSwitchCase="'comments'" class="form-linked-info" [stitch]="folder" />

    <stch-json-viewer *ngSwitchCase="'debug'" [json]="folder?.debug" />
  </app-scroll>

  @if (selectedTab === 'stitch' && hasKnowledgeHubAccess()) {
    <app-stitch-hub
      [stitch]="folder"
      (clickStitchItem)="handleClickItem($event)"
      (dblClickStitchItem)="handleDblClickItem($event)"
    />
  }

  <stch-save-cancel-panel
    [enabled]="!pristine"
    [uploading]="isUploading()"
    [saveLabel]="saveLabel()"
    (onCancel)="handleCancel()"
    (onSave)="handleSubmit()"
  />

</div>

<!-- stitch-app-shell breadcrumbs footer in the main view, as on every detail page. -->
@if (navigation.enabled && stitchItem?.id) {
  <app-detail-breadcrumbs
    [crumbs]="breadcrumbs()"
    [status]="form.controls.archived.value ? 'Archived' : form.controls.deleted.value ? 'Trash' : undefined"
  />
}
